// src/pages/DatevDashboard.tsx

import React, { useState, useEffect } from 'react';
import { supabase } from '../shared/services/supabase';
import { DatevSettings } from '../features/datev/components/DatevSettings';
import { DatevPayrollReports } from '../features/datev/components/DatevPayrollReports';
import { DatevExports } from '../features/datev/components/DatevExports';
import { DatevExchange } from '../features/datev/components/DatevExchange';
import { DatevEau } from '../features/datev/components/DatevEau';
import { DatevSyncStatus } from '../features/datev/components/DatevSyncStatus';
import { DatevEmployees } from '../features/datev/components/DatevEmployees';
import { EauUebersicht } from '../features/datev/components/EauUebersicht';
import { SteuerIdAntraege } from '../features/datev/components/SteuerIdAntraege';

interface DatevCustomer {
  id: string;
  company: string;
  beraternummer: string;
  mandantennummer: string;
  abrechnungsmethode: string;
}

// Merkt sich den zuletzt gewaehlten Mandanten lokal im Browser, damit die
// Auswahl einen Refresh ueberlebt. Existiert noch keine gespeicherte Auswahl
// oder ist der gespeicherte Mandant nicht mehr in der Liste, faellt es nach
// dem Laden auf den ersten Kunden der Liste zurueck.
const SELECTED_CUSTOMER_STORAGE_KEY = 'xtax:datev-dashboard:selected-customer-id';

const readStoredCustomerId = (): string => {
  try {
    return localStorage.getItem(SELECTED_CUSTOMER_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

type TabId = 'verbindung' | 'lohnauswertungen' | 'lohnergebnisse' | 'lohnaustausch' | 'eau' | 'eau-gesamt' | 'sync-status' | 'mitarbeiter' | 'steuer-id';

// Tabs, die ueber alle Mandanten hinweg arbeiten. Fuer sie ist die
// Mandantenauswahl im Kopf ohne Wirkung und wird deshalb ausgeblendet.
const MANDANTENUEBERGREIFENDE_TABS: TabId[] = ['eau-gesamt'];

export const DatevDashboard: React.FC = () => {
  const [customers, setCustomers] = useState<DatevCustomer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(() => readStoredCustomerId());
  const [activeTab, setActiveTab] = useState<TabId>('verbindung');
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);

  // Kunden mit Beraternummer laden
  useEffect(() => {
    const loadCustomers = async () => {
      setIsLoadingCustomers(true);
      try {
        const { data, error } = await supabase
          .from('customers')
          .select('id, company, beraternummer, mandantennummer, abrechnungsmethode')
          .not('beraternummer', 'is', null)
          .not('mandantennummer', 'is', null)
          .or('deleted.is.null,deleted.eq.false')
          .order('company');

        if (error) {
          console.error('Fehler beim Laden der Kunden:', error);
          return;
        }

        if (data && data.length > 0) {
          setCustomers(data);
          // Gespeicherte Auswahl wiederherstellen, sofern sie noch in der Liste
          // vorhanden ist; andernfalls den ersten Kunden waehlen.
          setSelectedCustomerId(prev => {
            const exists = prev !== '' && data.some(c => c.id === prev);
            return exists ? prev : data[0].id;
          });
        }
      } catch (err) {
        console.error('Fehler beim Laden der Kunden:', err);
      } finally {
        setIsLoadingCustomers(false);
      }
    };

    loadCustomers();
  }, []);

  // Auswahl lokal sichern, damit sie den Refresh ueberlebt.
  useEffect(() => {
    if (!selectedCustomerId) return;
    try {
      localStorage.setItem(SELECTED_CUSTOMER_STORAGE_KEY, selectedCustomerId);
    } catch {
      // localStorage nicht verfuegbar (z. B. privater Modus) - Sicherung entfaellt still.
    }
  }, [selectedCustomerId]);

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  const tabs = [
    { id: 'verbindung' as const, label: 'Verbindung' },
    { id: 'lohnauswertungen' as const, label: 'DATEV Lohnauswertungsdatenservice' },
    { id: 'lohnergebnisse' as const, label: 'DATEV Lohnergebnisdatenservice' },
    { id: 'lohnaustausch' as const, label: 'DATEV Lohnaustauschdatenservice' },
    { id: 'eau' as const, label: 'DATEV eAU-Abruf' },
    { id: 'eau-gesamt' as const, label: 'eAU gesamt' },
    { id: 'sync-status' as const, label: 'Sync-Status' },
    { id: 'mitarbeiter' as const, label: 'Mitarbeiter' },
    { id: 'steuer-id' as const, label: 'Steuer-ID-Anträge' }
  ];

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px 0' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#111827' }}>
          DATEV
        </h2>

        {/* Kunden-Auswahl. Bei mandantenuebergreifenden Tabs ausgeblendet,
            weil die Auswahl dort nichts bewirken wuerde. Der zuletzt
            gewaehlte Mandant bleibt im Zustand erhalten und ist beim
            Zurueckwechseln unveraendert gesetzt. */}
        {!MANDANTENUEBERGREIFENDE_TABS.includes(activeTab) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Mandant:
            </label>
            <select
              value={selectedCustomerId}
              onChange={e => setSelectedCustomerId(e.target.value)}
              disabled={isLoadingCustomers}
              style={{
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '300px',
                backgroundColor: 'white'
              }}
            >
              {isLoadingCustomers ? (
                <option>Lade Kunden...</option>
              ) : (
                customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.company} ({c.beraternummer}-{c.mandantennummer})
                  </option>
                ))
              )}
            </select>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        borderBottom: '2px solid #E5E7EB',
        marginBottom: '24px'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #2563EB' : '2px solid transparent',
              color: activeTab === tab.id ? '#2563EB' : '#6B7280',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? '600' : '400',
              cursor: 'pointer',
              marginBottom: '-2px',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Mandantenuebergreifender Inhalt. Steht ausserhalb der
          selectedCustomer-Bedingung, weil diese Ansicht ohne Mandantenauswahl
          arbeitet und auch dann etwas zeigen muss, wenn kein Mandant mit
          DATEV-Konfiguration vorhanden ist. */}
      {activeTab === 'eau-gesamt' && <EauUebersicht />}

      {/* Content */}
      {activeTab !== 'eau-gesamt' && selectedCustomer && (
        <div>
          {activeTab === 'verbindung' && (
            <DatevSettings
              customerId={selectedCustomer.id}
              beraternummer={selectedCustomer.beraternummer}
              mandantennummer={selectedCustomer.mandantennummer}
            />
          )}

          {activeTab === 'lohnauswertungen' && (
            <DatevPayrollReports
              customerId={selectedCustomer.id}
              beraternummer={selectedCustomer.beraternummer}
              mandantennummer={selectedCustomer.mandantennummer}
            />
          )}

          {activeTab === 'lohnergebnisse' && (
            <DatevExports
              customerId={selectedCustomer.id}
              beraternummer={selectedCustomer.beraternummer}
              mandantennummer={selectedCustomer.mandantennummer}
            />
          )}

          {activeTab === 'lohnaustausch' && (
            <DatevExchange
              customerId={selectedCustomer.id}
              beraternummer={selectedCustomer.beraternummer}
              mandantennummer={selectedCustomer.mandantennummer}
              datevLohnsystem={selectedCustomer.abrechnungsmethode}
            />
          )}

          {activeTab === 'eau' && (
            <DatevEau
              customerId={selectedCustomer.id}
              beraternummer={selectedCustomer.beraternummer}
              mandantennummer={selectedCustomer.mandantennummer}
            />
          )}

          {activeTab === 'sync-status' && (
            <DatevSyncStatus
              customerId={selectedCustomer.id}
              beraternummer={selectedCustomer.beraternummer}
              mandantennummer={selectedCustomer.mandantennummer}
            />
          )}

          {activeTab === 'mitarbeiter' && (
            <DatevEmployees
              customerId={selectedCustomer.id}
              beraternummer={selectedCustomer.beraternummer}
              mandantennummer={selectedCustomer.mandantennummer}
              datevLohnsystem={selectedCustomer.abrechnungsmethode}
            />
          )}

          {activeTab === 'steuer-id' && (
            <SteuerIdAntraege
              customerId={selectedCustomer.id}
              company={selectedCustomer.company}
            />
          )}
        </div>
      )}

      {/* Kein Kunde ausgewählt */}
      {activeTab !== 'eau-gesamt' && !selectedCustomer && !isLoadingCustomers && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#6B7280',
          fontSize: '14px',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px'
        }}>
          Kein Mandant mit DATEV-Konfiguration (Beraternummer + Mandantennummer) gefunden.
        </div>
      )}
    </div>
  );
};
